import { useEffect, useState } from 'react'
import { loadPlayerGameweekPicks } from '../api/players'
import { computeGameweekDetail } from '../season'
import { getGamesByGameweek, getActualResultsByGameweek } from '../seasonData'
import type { GameweekDetail } from '../types'

/**
 * Another player's scored gameweek, assembled from their picks.
 *
 * Fixtures and results are app-wide, so only the picks are per-player — one fetch of
 * `/players/:id/picks/:gameweek`. Two callers: the profile's Best-gameweek mark row, and the
 * results modal opened from it or from a chip tile.
 *
 * Keyed and matched on (userId, gameweek) the same defensive way `useViewedPlayer` keys its two
 * fetches: an in-flight response for a previously-viewed player can arrive after the screen has
 * moved on, and it must be ignored rather than briefly painted under the wrong name.
 */
export function useOtherPlayerGameweekDetail(
  userId: string | null | undefined,
  gameweek: number | null
): GameweekDetail | null {
  const [loaded, setLoaded] = useState<{
    userId: string
    gameweek: number
    detail: GameweekDetail
  } | null>(null)

  useEffect(() => {
    if (!userId || gameweek === null) return
    let cancelled = false
    loadPlayerGameweekPicks(userId, gameweek)
      .then((picks) => {
        if (cancelled) return
        const games = getGamesByGameweek()[gameweek] ?? []
        const results = getActualResultsByGameweek()[gameweek] ?? {}
        const detail = computeGameweekDetail(
          gameweek,
          games,
          picks.predictions,
          results,
          picks.bankerGameId,
          picks.activeChipId === 'doubleUp',
          picks.activeChipId === 'cleanSweep',
          picks.activeChipId === 'goalfest'
        )
        setLoaded({ userId, gameweek, detail })
      })
      .catch(() => {
        // Leave whatever is there; the modal renders its "no detail available" fallback.
      })
    return () => {
      cancelled = true
    }
  }, [userId, gameweek])

  return loaded && loaded.userId === userId && loaded.gameweek === gameweek
    ? loaded.detail
    : null
}
